/**
 * Procedural dungeon map generator.
 *
 * Goals for this demo:
 * - Always spawn the player at (playerStartX, playerStartY)
 * - Guarantee connectivity across the playable space
 * - Keep the same tile vocabulary the renderer already expects
 *   ('floor', 'corridor', 'door-open', 'door-locked', 'non-walkable', etc.)
 */

import {
  SHRINE_MIN_FLOOR,
  SHRINE_SPAWN_CHANCE_PER_FLOOR,
  SHRINE_TIER_WEIGHTS,
  rollShrineTier,
} from "../shrine/shrineConfig";

import { WELL_MIN_FLOOR, WELL_SPAWN_CHANCE_PER_FLOOR } from "../well/wellConfig";

import { isSpiderBossFloor } from '../boss/spiderBossConfig';
import { maybeApplySanctifiedStatueSecret } from './sanctifiedStatueSecret';


// Tiny deterministic RNG (Mulberry32)
function mulberry32(seed) {
  let t = seed >>> 0;
  return function rand() {
    t += 0x6D2B79F5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

function manhattan(a, b) {
  return Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
}

function inBounds(x, y, w, h) {
  return x >= 0 && y >= 0 && x < w && y < h;
}

function carveRect(map, x, y, w, h, tile) {
  for (let yy = y; yy < y + h; yy++) {
    for (let xx = x; xx < x + w; xx++) {
      if (map[yy] && map[yy][xx] !== undefined) map[yy][xx] = tile;
    }
  }
}

function carveLine(map, x1, y1, x2, y2, tile) {
  let x = x1;
  let y = y1;
  const dx = Math.sign(x2 - x1);
  const dy = Math.sign(y2 - y1);
  while (x !== x2) {
    if (map[y] && map[y][x] !== undefined && map[y][x] === 'non-walkable') map[y][x] = tile;
    x += dx;
  }
  while (y !== y2) {
    if (map[y] && map[y][x] !== undefined && map[y][x] === 'non-walkable') map[y][x] = tile;
    y += dy;
  }
  if (map[y] && map[y][x] !== undefined && map[y][x] === 'non-walkable') map[y][x] = tile;
}

function rectsOverlap(a, b, padding = 1) {
  return !(
    a.x + a.w + padding <= b.x ||
    b.x + b.w + padding <= a.x ||
    a.y + a.h + padding <= b.y ||
    b.y + b.h + padding <= a.y
  );
}

function roomCenter(r) {
  return {
    x: Math.floor(r.x + r.w / 2),
    y: Math.floor(r.y + r.h / 2),
  };
}

function getCandidates(map, allowSet) {
  const candidates = [];
  for (let y = 0; y < map.length; y++) {
    for (let x = 0; x < map[0].length; x++) {
      if (allowSet.has(map[y][x])) candidates.push({ x, y });
    }
  }
  return candidates;
}

function pick(rng, arr) {
  return arr.length ? arr[Math.floor(rng() * arr.length)] : null;
}

const STATUE_POOL_SIZE = 10;

function pickUniqueStatueIds(rng, count = 4) {
  const pool = Array.from({ length: STATUE_POOL_SIZE }, (_, i) => i + 1);

  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }

  return pool.slice(0, Math.max(0, count));
}

function getRandomStatueTile(rng) {
  return `statue-${1 + Math.floor(rng() * STATUE_POOL_SIZE)}`;
}
function postProcessOrganic(map, rooms, rng, width, height, playerStartX, playerStartY) {
  const dirs = [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ];
  const key = (x, y) => `${x},${y}`;

  // Protect:
  // - Any floor tile that touches a corridor (entrances)
  // - The starter area near the player start
  const protectedTiles = new Set();
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      if (map[y][x] !== 'floor') continue;
      for (const [dx, dy] of dirs) {
        if (map[y + dy]?.[x + dx] === 'corridor') {
          protectedTiles.add(key(x, y));
          break;
        }
      }
      if (Math.abs(x - playerStartX) + Math.abs(y - playerStartY) <= 2) {
        protectedTiles.add(key(x, y));
      }
    }
  }

  const canRemove = (x, y) => {
    if (!inBounds(x, y, width, height)) return false;
    if (map[y][x] !== 'floor') return false;
    if (protectedTiles.has(key(x, y))) return false;

    // Don't create paper-thin bottlenecks inside rooms:
    // if this tile is already a 1-wide connector (2 opposite neighbors), keep it.
    let walkN = 0;
    const n = { r: false, l: false, d: false, u: false };
    if (map[y][x + 1] === 'floor') { walkN++; n.r = true; }
    if (map[y][x - 1] === 'floor') { walkN++; n.l = true; }
    if (map[y + 1][x] === 'floor') { walkN++; n.d = true; }
    if (map[y - 1][x] === 'floor') { walkN++; n.u = true; }
    const isConnector = walkN === 2 && ((n.r && n.l) || (n.u && n.d));
    if (isConnector) return false;

    return true;
  };

  const removeFloor = (x, y) => {
    if (canRemove(x, y)) map[y][x] = 'non-walkable';
  };

  const canBulge = (x, y, nx, ny) => {
    if (!inBounds(nx, ny, width, height)) return false;
    if (map[ny][nx] !== 'non-walkable') return false;

    // Avoid bleeding floors right alongside corridors (keeps corridor silhouettes crisp).
    for (const [dx, dy] of dirs) {
      if (map[ny + dy]?.[nx + dx] === 'corridor') return false;
    }
    // Must still be "attached" to room.
    const attached = dirs.some(([dx, dy]) => map[ny + dy]?.[nx + dx] === 'floor');
    return attached;
  };

  // 1) Nibble corners (ruined stone feel).
  for (const r of rooms) {
    const corners = [
      { x: r.x, y: r.y, ix: 1, iy: 1 },
      { x: r.x + r.w - 1, y: r.y, ix: -1, iy: 1 },
      { x: r.x, y: r.y + r.h - 1, ix: 1, iy: -1 },
      { x: r.x + r.w - 1, y: r.y + r.h - 1, ix: -1, iy: -1 },
    ];
    for (const c of corners) {
      if (rng() < 0.75) removeFloor(c.x, c.y);
      // Chamfer: occasionally remove one adjacent edge tile.
      if (rng() < 0.35) {
        if (rng() < 0.5) removeFloor(c.x + c.ix, c.y);
        else removeFloor(c.x, c.y + c.iy);
      }
    }
  }

  // 2) Jagged edges (small bites along room borders).
  for (const r of rooms) {
    const bites = 2 + Math.floor(rng() * 5);
    for (let i = 0; i < bites; i++) {
      // Pick a random point on the room perimeter.
      const onHorizontal = rng() < 0.5;
      const x = onHorizontal
        ? r.x + Math.floor(rng() * r.w)
        : (rng() < 0.5 ? r.x : r.x + r.w - 1);
      const y = onHorizontal
        ? (rng() < 0.5 ? r.y : r.y + r.h - 1)
        : r.y + Math.floor(rng() * r.h);

      if (rng() < 0.20) removeFloor(x, y);
    }
  }

  // 3) Alcoves / bulges (a few one-tile protrusions).
  for (const r of rooms) {
    const bulges = 1 + Math.floor(rng() * 3);
    for (let i = 0; i < bulges; i++) {
      const x = r.x + Math.floor(rng() * r.w);
      const y = r.y + Math.floor(rng() * r.h);
      if (map[y][x] !== 'floor') continue;

      // Find outward candidates adjacent to this tile.
      const out = [];
      for (const [dx, dy] of dirs) {
        const nx = x + dx;
        const ny = y + dy;
        if (canBulge(x, y, nx, ny)) out.push({ nx, ny });
      }
      if (!out.length) continue;
      const p = out[Math.floor(rng() * out.length)];
      map[p.ny][p.nx] = 'floor';
    }
  }
}


/**
 * @param {object} args
 * @param {number} args.width
 * @param {number} args.height
 * @param {number} args.playerStartX
 * @param {number} args.playerStartY
 * @param {number} [args.seed] - Optional seed for deterministic floors.
 * @param {number} [args.floor] - Optional floor index.
 */
// ------------------------------------------------------------
// Floor size schedule (used only as a fallback if width/height are omitted)
// Floor 1: 20x20
// Floor 2: 30x30
// Floor 3+: 45x45
// ------------------------------------------------------------
function getFloorDimensions(floor) {
  const lvl = Math.max(1, Math.floor(Number(floor) || 1));
  if (lvl === 1) return { width: 20, height: 20 };
  if (lvl === 2) return { width: 30, height: 30 };
  return { width: 45, height: 45 };
}


function shuffleInPlace(rng, arr) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function isRectClear(map, x, y, w, h, padding = 1) {
  const height = map.length;
  const width = map[0]?.length || 0;

  for (let yy = y - padding; yy < y + h + padding; yy++) {
    for (let xx = x - padding; xx < x + w + padding; xx++) {
      if (xx < 0 || yy < 0 || xx >= width || yy >= height) return false;
      if (map[yy][xx] !== 'non-walkable') return false;
    }
  }
  return true;
}

function countAdjacentType(map, x, y, types) {
  let n = 0;
  if (types.has(map[y]?.[x + 1])) n++;
  if (types.has(map[y]?.[x - 1])) n++;
  if (types.has(map[y + 1]?.[x])) n++;
  if (types.has(map[y - 1]?.[x])) n++;
  return n;
}

function carveLabyrinthLayout({
  map,
  rooms,
  rng,
  width,
  height,
  playerStartX,
  playerStartY,
  moodLayout,
}) {
  const corridorTile = 'corridor';

  const mazeDirs = [
    [0, -2],
    [2, 0],
    [0, 2],
    [-2, 0],
  ];

  const startCell = {
  x: clamp((playerStartX | 1), 1, width - 2),
  y: clamp((playerStartY | 1), 1, height - 2),
};

map[startCell.y][startCell.x] = corridorTile;

  const visited = new Set();
  const key = (x, y) => `${x},${y}`;

  const stack = [{ x: startCell.x, y: startCell.y }];
  visited.add(key(startCell.x, startCell.y));

  while (stack.length) {
    const current = stack[stack.length - 1];
    const dirs = shuffleInPlace(rng, [...mazeDirs]);

    let carved = false;

    for (const [dx, dy] of dirs) {
      const nx = current.x + dx;
      const ny = current.y + dy;
      if (nx < 1 || ny < 1 || nx >= width - 1 || ny >= height - 1) continue;
      if (visited.has(key(nx, ny))) continue;

      const mx = current.x + dx / 2;
      const my = current.y + dy / 2;

      if (map[my][mx] !== 'non-walkable' || map[ny][nx] !== 'non-walkable') continue;

      map[my][mx] = corridorTile;
      map[ny][nx] = corridorTile;

      visited.add(key(nx, ny));
      stack.push({ x: nx, y: ny });
      carved = true;
      break;
    }

    if (!carved) stack.pop();
  }

  // Add a SMALL number of loops so it intertwines,
  // but keep plenty of dead ends.
  const loopPasses = clamp(
    Math.round(((width * height) / 250) * moodLayout.corridorDensityMultiplier),
    3,
    16
  );

  for (let i = 0; i < loopPasses; i++) {
    const x = 1 + Math.floor(rng() * (width - 2));
    const y = 1 + Math.floor(rng() * (height - 2));

    if (map[y][x] !== 'non-walkable') continue;

    const adjacentCorridors = countAdjacentType(
      map,
      x,
      y,
      new Set(['corridor', 'floor', 'steps-up'])
    );

    if (adjacentCorridors >= 2 && adjacentCorridors <= 3) {
      map[y][x] = corridorTile;
    }
  }

const starterW = 4;
const starterH = 4;
const starter = {
  x: clamp(playerStartX - Math.floor(starterW / 2), 1, width - starterW - 1),
  y: clamp(playerStartY - Math.floor(starterH / 2), 1, height - starterH - 1),
  w: starterW,
  h: starterH,
};

rooms.push(starter);
carveRect(map, starter.x, starter.y, starter.w, starter.h, 'floor');

  // Attach many tiny rooms to corridor walls.
  const roomShapes = [
    { w: 2, h: 3 },
    { w: 2, h: 4 },
    { w: 3, h: 3 },
    { w: 3, h: 4 },
    { w: 4, h: 4 },
  ];

  const targetExtraRooms = clamp(
    Math.round((12 + Math.sqrt(width * height) * 1.2) * moodLayout.roomCountMultiplier),
    10,
    40
  );

  const candidates = [];
  for (let y = 2; y < height - 2; y++) {
    for (let x = 2; x < width - 2; x++) {
      if (map[y][x] !== 'corridor') continue;

      candidates.push({ x, y, dx: 0, dy: -1 });
      candidates.push({ x, y, dx: 1, dy: 0 });
      candidates.push({ x, y, dx: 0, dy: 1 });
      candidates.push({ x, y, dx: -1, dy: 0 });
    }
  }

  shuffleInPlace(rng, candidates);

  for (const c of candidates) {
    if (rooms.length >= targetExtraRooms + 1) break;

    const shape = roomShapes[Math.floor(rng() * roomShapes.length)];
    let rx;
    let ry;

    if (c.dx === 1) {
      rx = c.x + 1;
      ry = c.y - Math.floor(shape.h / 2);
    } else if (c.dx === -1) {
      rx = c.x - shape.w;
      ry = c.y - Math.floor(shape.h / 2);
    } else if (c.dy === 1) {
      rx = c.x - Math.floor(shape.w / 2);
      ry = c.y + 1;
    } else {
      rx = c.x - Math.floor(shape.w / 2);
      ry = c.y - shape.h;
    }

if (!isRectClear(map, rx, ry, shape.w, shape.h, 0)) continue;

    carveRect(map, rx, ry, shape.w, shape.h, 'floor');
    rooms.push({ x: rx, y: ry, w: shape.w, h: shape.h });
  }

  // Add extra dead-end spurs after rooms are placed.
  const spurStarts = getCandidates(map, new Set(['corridor']));
  shuffleInPlace(rng, spurStarts);

  const spurCount = clamp(
    Math.round(spurStarts.length * 0.10 * moodLayout.deadEndMultiplier),
    8,
    80
  );

  for (let i = 0; i < spurCount && i < spurStarts.length; i++) {
    const start = spurStarts[i];
    if (manhattan(start, { x: playerStartX, y: playerStartY }) < 6) continue;

    let x = start.x;
    let y = start.y;
    let dir = shuffleInPlace(rng, [[1,0],[-1,0],[0,1],[0,-1]])[0];
    const len = 2 + Math.floor(rng() * 5);

    for (let step = 0; step < len; step++) {
      const nx = x + dir[0];
      const ny = y + dir[1];
      if (!inBounds(nx, ny, width, height)) break;
      if (map[ny][nx] !== 'non-walkable') break;

      const adjacent = countAdjacentType(map, nx, ny, new Set(['corridor', 'floor']));
      if (adjacent > 1) break;

      map[ny][nx] = corridorTile;
      x = nx;
      y = ny;

      if (rng() < 0.45) {
        dir = shuffleInPlace(rng, [[1,0],[-1,0],[0,1],[0,-1]])[0];
      }
    }
  }
}



export function generateMap({
  width,
  height,
  playerStartX,
  playerStartY,
  seed,
  floor,
  moodProfile = null,
  moodId = null,
}) {
// Normalize floor + provide default dimensions/spawn if omitted.
const floorLevel = Math.max(1, Math.floor(Number(floor) || 1));

// If width/height aren't provided, fall back to the floor schedule above.
if (!Number.isFinite(Number(width)) || Number(width) <= 0 || !Number.isFinite(Number(height)) || Number(height) <= 0) {
  const d = getFloorDimensions(floorLevel);
  width = d.width;
  height = d.height;
} else {
  width = Math.floor(Number(width));
  height = Math.floor(Number(height));
}

// Default spawn: center of the map.
if (!Number.isFinite(Number(playerStartX)) || !Number.isFinite(Number(playerStartY))) {
  playerStartX = Math.floor(width / 2);
  playerStartY = Math.floor(height / 2);
} else {
  playerStartX = Math.floor(Number(playerStartX));
  playerStartY = Math.floor(Number(playerStartY));
}

  const effectiveSeed = (seed ?? Date.now()) ^ ((floor ?? 1) * 0x9E3779B1);
  const rng = mulberry32(effectiveSeed);
  // Legacy static boss floor support is intentionally disabled.
  // The floor-4 spider boss now spawns dynamically after spiderweb interaction.
  const LEGACY_STATIC_BOSS_FLOOR = 0;
  const isBossFloor =
    LEGACY_STATIC_BOSS_FLOOR > 0 &&
    floorLevel === LEGACY_STATIC_BOSS_FLOOR &&
    !isSpiderBossFloor(floorLevel);
  const dirs = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];
  const isSpiderBossArenaFloor = isSpiderBossFloor(floorLevel);
  const isDeconstructedLayout = moodProfile?.layout?.preset === 'colossal';
const isLabyrinthianLayout = moodProfile?.layout?.preset === 'labyrinthian';
const allowAllSpawnsOnCorridors = isLabyrinthianLayout && !isSpiderBossArenaFloor;

  const moodLayout = {
    preset: moodProfile?.layout?.preset || 'default',
    forceSingleRoom: !!moodProfile?.layout?.forceSingleRoom,
    noCorridors: !!moodProfile?.layout?.noCorridors,
    roomSizeMultiplier: Math.max(
      0.25,
      Number(moodProfile?.layout?.roomSizeMultiplier ?? 1)
    ),
    roomCountMultiplier: Math.max(
      0.1,
      Number(moodProfile?.layout?.roomCountMultiplier ?? 1)
    ),
    corridorDensityMultiplier: Math.max(
      0,
      Number(moodProfile?.layout?.corridorDensityMultiplier ?? 1)
    ),
    corridorLengthMultiplier: Math.max(
      0,
      Number(moodProfile?.layout?.corridorLengthMultiplier ?? 1)
    ),
    deadEndMultiplier: Math.max(
      0,
      Number(moodProfile?.layout?.deadEndMultiplier ?? 1)
    ),
  };


  const isSingleRoomArenaLayout =
    isSpiderBossArenaFloor || (moodLayout.forceSingleRoom && moodLayout.noCorridors);
  const isSpiderBossArena = isSpiderBossArenaFloor;

  const moodCounts = {
    enemyMultiplier: Math.max(0, Number(moodProfile?.counts?.enemyMultiplier ?? 1)),
    trapMultiplier: Math.max(0, Number(moodProfile?.counts?.trapMultiplier ?? 1)),
    brazierMultiplier: Math.max(0, Number(moodProfile?.counts?.brazierMultiplier ?? 1)),
    restMultiplier: Math.max(0, Number(moodProfile?.counts?.restMultiplier ?? 1)),
    chestMultiplier: Math.max(0, Number(moodProfile?.counts?.chestMultiplier ?? 1)),
    chestFlatBonus: Math.floor(Number(moodProfile?.counts?.chestFlatBonus ?? 0)),
    simpleChestCount: Math.max(0, Math.floor(Number(moodProfile?.counts?.simpleChestCount ?? 1))),
    loreMultiplier: Math.max(0, Number(moodProfile?.counts?.loreMultiplier ?? 1)),
    shopMultiplier: Math.max(0, Number(moodProfile?.counts?.shopMultiplier ?? 1)),
    lockedDoorMultiplier: Math.max(0, Number(moodProfile?.counts?.lockedDoorMultiplier ?? 1)),
    lockedDoorFlatBonus: Math.floor(Number(moodProfile?.counts?.lockedDoorFlatBonus ?? 0)),
    storylineMultiplier: Math.max(0, Number(moodProfile?.counts?.storylineMultiplier ?? 1)),
    statueMultiplier: Math.max(0, Number(moodProfile?.counts?.statueMultiplier ?? 0)),
    statueFlatBonus: Math.floor(Number(moodProfile?.counts?.statueFlatBonus ?? 0)),
    statueSpawnChance: clamp(Number(moodProfile?.counts?.statueSpawnChance ?? 1), 0, 1),
  };

// --- Size scaling (base layout was tuned for 18x18) ---
const BASE_W = 18;
const BASE_H = 18;
const baseArea = BASE_W * BASE_H;
const area = width * height;
const areaScale = area / baseArea;         // 54x54 => 9
const linearScale = Math.sqrt(areaScale);  // 54x54 => 3


  // 1) Start with solid walls.
  let map = Array.from({ length: height }, () => Array(width).fill('non-walkable'));

  // 2) Rooms: guarantee a central starter room around the player start.
  const rooms = [];
  if (isSingleRoomArenaLayout) {
  const arenaMargin = 1;
  const arenaRoom = {
    x: arenaMargin,
    y: arenaMargin,
    w: Math.max(1, width - (arenaMargin * 2)),
    h: Math.max(1, height - (arenaMargin * 2)),
    shape: 'rect',
  };

  carveRect(map, arenaRoom.x, arenaRoom.y, arenaRoom.w, arenaRoom.h, 'floor');
  rooms.push(arenaRoom);
} else if (isDeconstructedLayout) {
  const circleCx = Math.floor(width / 2);
  const circleCy = Math.floor(height / 2);
  const circleRadius = Math.max(6, Math.floor((Math.min(width, height) - 4) / 2));

  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const dx = x - circleCx;
      const dy = y - circleCy;

      if ((dx * dx) + (dy * dy) <= (circleRadius * circleRadius)) {
        map[y][x] = 'floor';
      }
    }
  }

  rooms.push({
    x: clamp(circleCx - circleRadius, 1, width - 2),
    y: clamp(circleCy - circleRadius, 1, height - 2),
    w: clamp((circleRadius * 2) + 1, 1, width - 2),
    h: clamp((circleRadius * 2) + 1, 1, height - 2),
    shape: 'circle',
    cx: circleCx,
    cy: circleCy,
    radius: circleRadius,
  });
} else if (isLabyrinthianLayout) {
  carveLabyrinthLayout({
    map,
    rooms,
    rng,
    width,
    height,
    playerStartX,
    playerStartY,
    moodLayout,
  });
} else {
  const starterW = clamp(4 + Math.floor(rng() * 2), 4, 6);
  const starterH = clamp(4 + Math.floor(rng() * 2), 4, 6);
  const starter = {
    x: clamp(playerStartX - Math.floor(starterW / 2), 1, width - starterW - 1),
    y: clamp(playerStartY - Math.floor(starterH / 2), 1, height - starterH - 1),
    w: starterW,
    h: starterH,
  };
  rooms.push(starter);
  carveRect(map, starter.x, starter.y, starter.w, starter.h, 'floor');

  // Place additional random rooms (scaled to map size + mood).
const baseMinRooms = clamp(Math.round(6 * linearScale), 6, 70);
const baseMaxRooms = clamp(Math.round(9 * linearScale), baseMinRooms, 100);

const minRooms = clamp(
  Math.round(baseMinRooms * moodLayout.roomCountMultiplier),
  1,
  120
);
const maxRooms = clamp(
  Math.round(baseMaxRooms * moodLayout.roomCountMultiplier),
  minRooms,
  140
);
const targetRooms = minRooms + Math.floor(rng() * (maxRooms - minRooms + 1));

// More rooms => more placement attempts.
const attempts = clamp(Math.round(targetRooms * 25), 80, 2200);

// Slightly scale room sizes so larger maps don't feel like tiny crumbs,
// then let the mood shrink/enlarge them.
const baseRoomMin = clamp(3 + Math.floor(linearScale * 0.75), 3, 10);
const ROOM_MAX_CAP = 6;
const baseRoomMax = clamp(7 + Math.floor(linearScale * 1.5), baseRoomMin + 1, ROOM_MAX_CAP);

const roomMin = clamp(
  Math.max(2, Math.round(baseRoomMin * moodLayout.roomSizeMultiplier)),
  2,
  12
);
const roomMax = clamp(
  Math.max(roomMin + 1, Math.round(baseRoomMax * moodLayout.roomSizeMultiplier)),
  roomMin + 1,
  14
);

for (let i = 0; i < attempts && rooms.length < targetRooms; i++) {
  const w = clamp(roomMin + Math.floor(rng() * (roomMax - roomMin + 1)), roomMin, roomMax);
  const h = clamp(roomMin + Math.floor(rng() * (roomMax - roomMin + 1)), roomMin, roomMax);
    const x = 1 + Math.floor(rng() * (width - w - 2));
    const y = 1 + Math.floor(rng() * (height - h - 2));
    const candidate = { x, y, w, h };
    if (rooms.some(r => rectsOverlap(r, candidate, 1))) continue;
    rooms.push(candidate);
    carveRect(map, x, y, w, h, 'floor');
  }

  // 3) Corridors: connect rooms via their centers.
  // Sort by distance to center and connect in that order for a decent "spine".
  const center = { x: Math.floor(width / 2), y: Math.floor(height / 2) };
  const roomOrder = [...rooms].sort((a, b) => manhattan(roomCenter(a), center) - manhattan(roomCenter(b), center));
  for (let i = 1; i < roomOrder.length; i++) {
    const a = roomCenter(roomOrder[i - 1]);
    const b = roomCenter(roomOrder[i]);
    // 50/50 whether we go horizontal-first or vertical-first.
    if (rng() < 0.5) {
      carveLine(map, a.x, a.y, b.x, a.y, 'corridor');
      carveLine(map, b.x, a.y, b.x, b.y, 'corridor');
    } else {
      carveLine(map, a.x, a.y, a.x, b.y, 'corridor');
      carveLine(map, a.x, b.y, b.x, b.y, 'corridor');
    }
  }


// 3.25) Extra corridor complexity (scaled):
// - Add a few extra links to create loops (less "string of beads")
// - Add short dead-end branches so corridors feel less grid-perfect
{
  const dirs4 = [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ];

  // Extra links (loops)
  const extraLinks = clamp(
  Math.round(rooms.length * 0.35 * moodLayout.corridorDensityMultiplier),
  0,
  Math.max(0, Math.round(rooms.length * 0.9 * moodLayout.corridorDensityMultiplier))
);

  for (let i = 0; i < extraLinks; i++) {
    const ra = pick(rng, rooms);
    const rb = pick(rng, rooms);
    if (!ra || !rb || ra === rb) continue;

    const a = roomCenter(ra);
    const b = roomCenter(rb);

    if (rng() < 0.5) {
      carveLine(map, a.x, a.y, b.x, a.y, 'corridor');
      carveLine(map, b.x, a.y, b.x, b.y, 'corridor');
    } else {
      carveLine(map, a.x, a.y, a.x, b.y, 'corridor');
      carveLine(map, a.x, b.y, b.x, b.y, 'corridor');
    }
  }

  // Dead-end branches (spurs)
  const branchCount = clamp(
  Math.round(rooms.length * 0.5 * moodLayout.deadEndMultiplier),
  0,
  80
);

const baseBranchLenMin = 3;
const baseBranchLenMax = clamp(6 + Math.floor(linearScale * 2), 6, 14);

const branchLenMin = Math.max(
  1,
  Math.round(baseBranchLenMin * Math.max(0.5, moodLayout.corridorLengthMultiplier))
);

const branchLenMax = Math.max(
  branchLenMin,
  Math.round(baseBranchLenMax * Math.max(0.5, moodLayout.corridorLengthMultiplier))
);
  const entrance = { x: playerStartX, y: playerStartY };

  const corridorTiles = getCandidates(map, new Set(['corridor']));

  const carveBranch = (sx, sy, len) => {
    let x = sx;
    let y = sy;
    let dir = dirs4[Math.floor(rng() * dirs4.length)];

    for (let step = 0; step < len; step++) {
      // Occasionally turn to avoid perfectly straight spurs.
      if (rng() < 0.25) dir = dirs4[Math.floor(rng() * dirs4.length)];

      const nx = x + dir[0];
      const ny = y + dir[1];
      if (!inBounds(nx, ny, width, height)) break;

      // Only carve into solid walls (never overwrite rooms / specials).
      if (map[ny][nx] !== 'non-walkable') break;

      map[ny][nx] = 'corridor';
      x = nx;
      y = ny;

      // Rarely fork a tiny side-step (adds "complex" silhouette).
      if (rng() < 0.10) {
        const side = dirs4[Math.floor(rng() * dirs4.length)];
        const fx = x + side[0];
        const fy = y + side[1];
        if (inBounds(fx, fy, width, height) && map[fy][fx] === 'non-walkable') {
          map[fy][fx] = 'corridor';
        }
      }
    }
  };

  for (let i = 0; i < branchCount; i++) {
    const start = pick(rng, corridorTiles);
    if (!start) break;

    // Keep spurs away from the spawn so the starter area stays readable.
    if (manhattan(start, entrance) < 6) continue;

    const len = branchLenMin + Math.floor(rng() * (branchLenMax - branchLenMin + 1));
    carveBranch(start.x, start.y, len);
  }
}

  // 3.5) Organic pass: roughen room edges (ruined stone / cracked dungeon feel)
  postProcessOrganic(map, rooms, rng, width, height, playerStartX, playerStartY);
}

if (!isDeconstructedLayout && !moodLayout.noCorridors) {
    // 4) Doors: place doors at true room thresholds (room entrances).
  //
  // In this dungeon, corridors are carved *outside* rooms and stop at the room
  // boundary (rooms remain 'floor'). That means the most natural "threshold"
  // tile is the *perimeter floor tile* that touches a corridor.
  //
  // We:
  // - Collect entrance candidates on room perimeters where floor touches corridor
  // - Prefer spreading doors across different rooms
  // - Place 1..3 doors total (cap at 3 so your default 3 keys always suffices)
  // - Randomize locked/open, but only lock entrances that appear to be optional
  //   (corridor endpoint entrances), so we don't gate the main path.
  
  const entrancePos = { x: playerStartX, y: playerStartY };

  // Helper: find which room a tile belongs to (by rect bounds).
  const roomIndexAt = (x, y) => {
    for (let i = 0; i < rooms.length; i++) {
      const r = rooms[i];
      if (x >= r.x && x < r.x + r.w && y >= r.y && y < r.y + r.h) return i;
    }
    return -1;
  };

  // Helper: how many adjacent corridor tiles does a corridor tile have?
  const corridorDegree = (x, y) => {
    let n = 0;
    for (const [dx, dy] of dirs) {
      if (map[y + dy]?.[x + dx] === 'corridor') n++;
    }
    return n;
  };

  // --- Double-door helpers (with a tiny guard) ---

// If corridor tile (x,y) touches the target room on exactly ONE side,
// return that direction [dx,dy]. If it touches 0 or >1 sides, return null.
const getRoomFacingDir = (x, y, roomId) => {
  const hits = [];
  for (const [dx, dy] of dirs) {
    const nx = x + dx;
    const ny = y + dy;
    if (map[ny]?.[nx] !== 'floor') continue;
    if (roomIndexAt(nx, ny) === roomId) hits.push([dx, dy]);
  }
  return hits.length === 1 ? hits[0] : null;
};

// Treat corridor or an already-placed door as "hallway".
const isHall = (t) => t === 'corridor' || t === 'door-open' || t === 'door-locked';

const hasHallwayInDir = (x, y, dx, dy) => {
  const nx = x + dx;
  const ny = y + dy;
  return isHall(map[ny]?.[nx]);
};

// Place a door tile on the chosen corridor tile AND (optionally) on the adjacent twin lane.
// Guard: only do twin-door if it's a clean, unambiguous 2-wide entrance.
const placeDoorWithTwin = (x, y, roomId, doorTile) => {
  const setDoor = (tx, ty) => {
    const t = map[ty]?.[tx];
    if (t === 'corridor') map[ty][tx] = doorTile;
  };

  // Always place the primary door.
  setDoor(x, y);

  // ---- tiny guard #1: must touch the room on exactly one side ----
  const roomDir = getRoomFacingDir(x, y, roomId);
  if (!roomDir) return;

  const [rdx, rdy] = roomDir;
  const awayDx = -rdx;
  const awayDy = -rdy;

  // ---- tiny guard #2: corridor must actually continue away from the room ----
  if (!hasHallwayInDir(x, y, awayDx, awayDy)) return;

  // Sideways directions (perpendicular to roomDir). This finds the "other lane".
  const sideDirs = (rdx === 0) ? [[1, 0], [-1, 0]] : [[0, 1], [0, -1]];

  for (const [sdx, sdy] of sideDirs) {
    const nx = x + sdx;
    const ny = y + sdy;

    // twin must be corridor
    if (map[ny]?.[nx] !== 'corridor') continue;

    // ---- tiny guard #3: twin must touch the SAME room on the SAME side ----
    const nRoomDir = getRoomFacingDir(nx, ny, roomId);
    if (!nRoomDir) continue;
    if (nRoomDir[0] !== rdx || nRoomDir[1] !== rdy) continue;

    // ---- tiny guard #4: twin must also continue away from the room ----
    if (!hasHallwayInDir(nx, ny, awayDx, awayDy)) continue;

    // ---- tiny guard #5: avoid junctions (don’t double-door near branches) ----
    if (corridorDegree(nx, ny) > 2) continue;

    // If all guards pass, it’s a true 2-wide entrance: place the second door.
    setDoor(nx, ny);
  }
};

  // Collect "true threshold" candidates:
  // For every corridor tile, look for adjacent floor tiles (room-side thresholds).
  // This works even after we roughen room edges (rooms are no longer perfect rectangles).
  const doorCandidates = [];
const seen = new Set();

for (let y = 1; y < height - 1; y++) {
  for (let x = 1; x < width - 1; x++) {
    if (map[y][x] !== 'corridor') continue;

    // Doors feel best at room thresholds.
    // - Endpoints (deg===1) are "lockable" optional entrances.
    // - Non-junction thresholds (deg===2) are open doors for ambience.
    const deg = corridorDegree(x, y);
    if (deg >= 3) continue; // skip junctions

    const lockable = deg === 1;

    // ✅ Must touch a room floor tile
    let touchedRoomId = -1;
    for (const [dx, dy] of dirs) {
      const nx = x + dx;
      const ny = y + dy;
      if (map[ny][nx] !== 'floor') continue;

      // Avoid gating right next to spawn (starter room feels better open).
      if (manhattan({ x: nx, y: ny }, entrancePos) <= 2) continue;

      const roomId = roomIndexAt(nx, ny);
      if (roomId >= 0) {
        touchedRoomId = roomId;
        break;
      }
    }
    if (touchedRoomId < 0) continue;

    const k = `${x},${y}`;
    if (seen.has(k)) continue;
    seen.add(k);

    // Endpoint doors are naturally "lockable" optional entrances
    doorCandidates.push({
      x,
      y,
      roomId: touchedRoomId,
      lockable,
      priority: lockable ? 2 : 1,
    });
  }
}


  // If somehow we found no candidates (tiny maps), fall back to corridor-adjacent logic.
  if (doorCandidates.length === 0) {
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        if (map[y][x] !== 'corridor') continue;
        const touchesFloor = dirs.some(([dx, dy]) => map[y + dy][x + dx] === 'floor');
        if (!touchesFloor) continue;
        if (manhattan({ x, y }, entrancePos) <= 2) continue;
        const lockable = corridorDegree(x, y) <= 1;
        doorCandidates.push({ x, y, roomId: roomIndexAt(x, y), lockable, priority: lockable ? 2 : 1 });
      }
    }
  }

  // Place MORE doors on larger floors.
// We keep locked doors capped to your key budget, but add extra OPEN doors
// so the big map feels "architected" instead of wide empty halls.
const MAX_LOCKED_DOORS = 3;
const maxDoors = clamp(Math.round(3 * linearScale), 3, 18); // 54x54 => ~9
const baseDoorCount = clamp(Math.round(rooms.length * 0.45), 3, maxDoors);

// Small deterministic wobble so floors don't all have identical counts.
const wobble = (rng() < 0.5 ? -1 : 1) * Math.floor(rng() * 2); // -0..-1 or 0..1
const desiredDoorCount = Math.min(
  doorCandidates.length,
  clamp(baseDoorCount + wobble, 2, maxDoors)
);

  // Deterministic shuffle, then sort by priority so we keep randomness within tiers.
  for (let i = doorCandidates.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [doorCandidates[i], doorCandidates[j]] = [doorCandidates[j], doorCandidates[i]];
  }
  doorCandidates.sort((a, b) => b.priority - a.priority);

  // Prefer spreading across rooms so doors feel intentionally placed.
  const chosen = [];
  const usedRooms = new Set();
  for (const c of doorCandidates) {
    if (chosen.length >= desiredDoorCount) break;
    if (c.roomId >= 0 && usedRooms.has(c.roomId)) continue;
    chosen.push(c);
    if (c.roomId >= 0) usedRooms.add(c.roomId);
  }
  // If we still need more doors, allow duplicates.
  for (const c of doorCandidates) {
    if (chosen.length >= desiredDoorCount) break;
    if (chosen.some(p => p.x === c.x && p.y === c.y)) continue;
    chosen.push(c);
  }

  // Choose which of the chosen doors are locked.
  // Keep it random, but only lock "lockable" entrances (optional side rooms),
  // and never exceed 3 locked doors (keys coverage).
  let baseLockedRemaining = 0;
if (chosen.length > 0) {
  const roll = rng();
  if (roll < 0.15) baseLockedRemaining = 0;
  else if (roll < 0.60) baseLockedRemaining = 1;
  else if (roll < 0.85) baseLockedRemaining = 2;
  else baseLockedRemaining = 3;
}

const maxLockableChosen = chosen.filter((c) => c.lockable).length;

let lockedRemaining = clamp(
  Math.round(baseLockedRemaining * moodCounts.lockedDoorMultiplier) +
    moodCounts.lockedDoorFlatBonus,
  0,
  Math.min(MAX_LOCKED_DOORS, maxLockableChosen)
);

  for (const c of chosen) {
  const doorTile = (lockedRemaining > 0 && c.lockable) ? 'door-locked' : 'door-open';
  if (doorTile === 'door-locked') lockedRemaining--;

  // Place door on the chosen lane AND on the twin lane if this is a true 2-wide entrance.
  placeDoorWithTwin(c.x, c.y, c.roomId, doorTile);
}

}
  // --- Helpers for placing interactables ---
  const isRoomy = (x, y) => {
    // A tile is "room-like" if it has at least 2 adjacent floor tiles.
    let adjacentFloors = 0;
    for (const [dx, dy] of dirs) {
      const nx = x + dx;
      const ny = y + dy;
      if (inBounds(nx, ny, width, height) && map[ny][nx] === 'floor') adjacentFloors++;
    }
    return adjacentFloors >= 2;
  };

  const placeInRoom = (tile, options = {}) => {
  const {
    avoid = [],
    minDistFrom = null,
    minDist = 0,
    allow = new Set(['floor']),
    tries = 400,
    requireRoomy = true,
  } = options;

  if (allowAllSpawnsOnCorridors) {
    const widenedAllow = new Set(allow);
    widenedAllow.add('corridor');

    return placeAnywhere(tile, {
      avoid,
      minDistFrom,
      minDist,
      allow: widenedAllow,
      tries: Math.max(tries, 1200),
    });
  }

  for (let i = 0; i < tries; i++) {
    const r = pick(rng, rooms);
    if (!r) break;
    const x = r.x + Math.floor(rng() * r.w);
    const y = r.y + Math.floor(rng() * r.h);
    if (!allow.has(map[y][x])) continue;
    if (requireRoomy && !isRoomy(x, y)) continue;
    if (avoid.some(p => p.x === x && p.y === y)) continue;
    if (minDistFrom && manhattan({ x, y }, minDistFrom) < minDist) continue;
    map[y][x] = tile;
    return { x, y };
  }
  return null;
};

  const placeAnywhere = (tile, options = {}) => {
    const { allow = new Set(['floor', 'corridor']), tries = 600, avoid = [], minDistFrom = null, minDist = 0 } = options;
    const candidates = getCandidates(map, allow).filter(p => !avoid.some(a => a.x === p.x && a.y === p.y));
    for (let i = 0; i < tries; i++) {
      const p = pick(rng, candidates);
      if (!p) return null;
      if (minDistFrom && manhattan(p, minDistFrom) < minDist) continue;
      if (map[p.y][p.x] !== 'floor' && map[p.y][p.x] !== 'corridor') continue;
      map[p.y][p.x] = tile;
      return p;
    }
    return null;
  };

  const placeAdjacentTo = (tile, origin, options = {}) => {
    const {
      avoid = [],
      allow = new Set(['floor', 'corridor']),
      minDistFrom = null,
      minDist = 0,
    } = options;

    if (!origin) return null;

    const candidates = [
      { x: origin.x + 1, y: origin.y },
      { x: origin.x - 1, y: origin.y },
      { x: origin.x, y: origin.y + 1 },
      { x: origin.x, y: origin.y - 1 },
    ];

    for (let i = candidates.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
    }

    for (const p of candidates) {
      if (!inBounds(p.x, p.y, width, height)) continue;
      if (!allow.has(map[p.y][p.x])) continue;
      if (avoid.some((a) => a.x === p.x && a.y === p.y)) continue;
      if (minDistFrom && manhattan(p, minDistFrom) < minDist) continue;

      map[p.y][p.x] = tile;
      return p;
    }

    return null;
  };

  // 5) Entrance/exit.
  // Always spawn at the requested start coords.
  map[playerStartY][playerStartX] = 'steps-up';
  const entrance = { x: playerStartX, y: playerStartY };

  // Choose an exit far from entrance, inside a room.
  // NOTE: placement can fail on small/skinny layouts or when rooms cluster
  // near the entrance. Guarantee a steps-down tile via progressive fallbacks.
  const targetExitDist = Math.floor((width + height) / 3);
  let exitPos = placeInRoom('steps-down', {
    avoid: [entrance],
    minDistFrom: entrance,
    minDist: targetExitDist,
  });

  // Relax: still in a room, but allow less "roomy" tiles and shorter distance.
  if (!exitPos) {
    exitPos = placeInRoom('steps-down', {
      avoid: [entrance],
      minDistFrom: entrance,
      minDist: Math.max(6, Math.floor(targetExitDist * 0.6)),
      requireRoomy: false,
      tries: 900,
    });
  }

  // Relax: allow corridor tiles too.
  if (!exitPos) {
    exitPos = placeAnywhere('steps-down', {
      allow: new Set(['floor', 'corridor']),
      avoid: [entrance],
      tries: 1200,
    });
  }

  // Last resort: pick the farthest walkable tile from the entrance.
  if (!exitPos) {
    const candidates = getCandidates(map, new Set(['floor', 'corridor', 'door-open']));
    let best = null;
    let bestD = -1;
    for (const p of candidates) {
      if (p.x === entrance.x && p.y === entrance.y) continue;
      const d = manhattan(p, entrance);
      if (d > bestD) {
        bestD = d;
        best = p;
      }
    }
    if (best) {
      map[best.y][best.x] = 'steps-down';
      exitPos = best;
    }
  }

  // 6) Interactables (counts mimic your original demo).
  // Keep these in rooms where possible so they feel intentional.
  const occupied = [{ ...entrance }];
  if (exitPos) occupied.push(exitPos);

  const entranceMerchantPos = isSpiderBossArena
    ? placeAdjacentTo('merchant', entrance, {
        avoid: occupied,
        allow: new Set(['floor']),
      })
    : null;

  if (entranceMerchantPos) occupied.push(entranceMerchantPos);

// ---- Size-scaled interactable counts (base tuned for 18x18) ----
// NOTE: For "density" objects (enemies, traps, braziers) we scale by AREA,
// so a 54x54 map (9x the area) gets ~9x the objects.
// For "service/POI" objects (rests, lore, story beats) we scale slower.

const baseRestCount = clamp(Math.round((2 * linearScale) + (0.25 * areaScale)), 2, 12);
const baseChestCount = clamp(Math.round((1 * linearScale) / 2 + 0.5), 1, 2);
const baseBrazierCount = clamp(Math.round(3 * areaScale), 3, 80);
const baseTrapCount = clamp(Math.round(2 * areaScale), 2, 45);
const baseMerchantCount = clamp(Math.round(1 * linearScale), 1, 2);
const baseEnemyCount = clamp(Math.round(4 * areaScale), 4, 90);
const baseBookshelfCount = clamp(Math.round(1 * linearScale + 1), 1, 10);

const restCount = clamp(
  Math.round(baseRestCount * moodCounts.restMultiplier),
  0,
  20
);

const chestCount = clamp(
  Math.round(baseChestCount * moodCounts.chestMultiplier) + moodCounts.chestFlatBonus,
  0,
  8
);

const simpleChestCount = floorLevel >= 2
  ? clamp(moodCounts.simpleChestCount, 0, 8)
  : 0;

const brazierCount = clamp(
  Math.round(baseBrazierCount * moodCounts.brazierMultiplier),
  0,
  120
);

const trapCount = clamp(
  Math.round(baseTrapCount * moodCounts.trapMultiplier),
  0,
  80
);

const merchantCount = isSpiderBossArena
  ? 0
  : clamp(
      Math.round(baseMerchantCount * moodCounts.shopMultiplier),
      0,
      4
    );

const enemyCount = isSpiderBossArena
  ? 0
  : clamp(
      Math.max(isBossFloor ? 1 : 0, Math.round(baseEnemyCount * moodCounts.enemyMultiplier)),
      isBossFloor ? 1 : 0,
      140
    );

const bookshelfCount = clamp(
  Math.round(baseBookshelfCount * moodCounts.loreMultiplier),
  0,
  16
);

const baseStorylineCount = clamp(
  Math.round((0.5 * linearScale) + 0.5),
  1,
  4
);

const baseStatueCount = clamp(
  Math.round(1.5 * linearScale),
  1,
  6
);

const storylineCount = clamp(
  Math.round(baseStorylineCount * moodCounts.storylineMultiplier),
  0,
  6
);

const statueCount = clamp(
  Math.round(baseStatueCount * moodCounts.statueMultiplier) + moodCounts.statueFlatBonus,
  0,
  12
);

// Resting sites (scaled)
for (let i = 0; i < restCount; i++) {
  const p = placeInRoom('rest', {
    avoid: occupied,
    minDistFrom: entrance,
    minDist: 3,
    tries: 900,
  });
  if (p) occupied.push(p);
}

// Chests (scaled, max 2 — secret annex chest is separate)
for (let i = 0; i < chestCount; i++) {
  const chestTile = rng() < 0.25 ? 'chest-closed' : 'chest-locked';
  const p = placeInRoom(chestTile, {
    avoid: occupied,
    minDistFrom: entrance,
    minDist: 4,
    tries: 1200,
  });
  if (p) occupied.push(p);
}

// Simple chests (floor 2+ only)
for (let i = 0; i < simpleChestCount; i++) {
  const chestTile = rng() < 0.25 ? 'chest-simple-closed' : 'chest-simple-locked';
  const p = placeInRoom(chestTile, {
    avoid: occupied,
    minDistFrom: entrance,
    minDist: 4,
    tries: 1200,
  });
  if (p) occupied.push(p);
}

// Braziers (scaled) – can be in corridors too
for (let i = 0; i < brazierCount; i++) {
  const p = placeAnywhere('brazier', {
    avoid: occupied,
    allow: new Set(['floor', 'corridor']),
    tries: 2200,
  });
  if (p) occupied.push(p);
}

// Traps (scaled) – mix rooms + corridors (keeps exploration spicy).
// Traps are walkable tiles; the gameplay handles the QTE/trigger on entry.
for (let i = 0; i < trapCount; i++) {
  const preferCorridor = rng() < 0.55;
  let p = placeAnywhere('trap', {
    avoid: occupied,
    allow: preferCorridor ? new Set(['corridor']) : new Set(['floor', 'corridor']),
    minDistFrom: entrance,
    minDist: 3,
    tries: 1800,
  });
  // Fallback: if corridor-only placement fails (or map has few corridors), allow rooms.
  if (!p) {
    p = placeAnywhere('trap', {
      avoid: occupied,
      allow: new Set(['floor', 'corridor']),
      minDistFrom: entrance,
      minDist: 3,
      tries: 1800,
    });
  }
  if (p) occupied.push(p);
}

// Merchants (scaled, max 2)
{
  const merchants = [];
  for (let i = 0; i < merchantCount; i++) {
    const minDistFrom = i === 0 ? entrance : merchants[0];
    const minDist = i === 0 ? 5 : 10; // keep the second merchant away from the first
    const p = placeInRoom('merchant', {
      avoid: occupied,
      minDistFrom,
      minDist,
      tries: 1400,
    });
    if (p) {
      occupied.push(p);
      merchants.push(p);
    }
  }
}

// The Witch appears exactly once per floor from floor 5 onward.
// She is intentionally placed before the secret annex is carved, so she can
// never land inside the hidden corridor or annex room.
if (floorLevel >= 5) {
  let witchPos = placeInRoom('witch', {
    avoid: occupied,
    minDistFrom: entrance,
    minDist: 4,
    tries: 1800,
  });

  if (!witchPos) {
    witchPos = placeAnywhere('witch', {
      avoid: occupied,
      allow: new Set(['floor', 'corridor']),
      minDistFrom: entrance,
      minDist: 4,
      tries: 2200,
    });
  }

  // Final failsafe: ensure the Witch still spawns on every eligible floor even
  // if the random placement helpers fail to find a candidate in a dense layout.
  if (!witchPos) {
    const floorCandidates = getCandidates(map, new Set(['floor'])).filter((p) => {
      if (occupied.some((a) => a.x === p.x && a.y === p.y)) return false;
      if (manhattan(p, entrance) < 4) return false;
      return isRoomy(p.x, p.y);
    });

    const corridorCandidates = getCandidates(map, new Set(['corridor'])).filter((p) => {
      if (occupied.some((a) => a.x === p.x && a.y === p.y)) return false;
      if (manhattan(p, entrance) < 4) return false;
      return true;
    });

    const fallbackCandidates = floorCandidates.length ? floorCandidates : corridorCandidates;
    const fallbackPos = pick(rng, fallbackCandidates);

    if (fallbackPos) {
      map[fallbackPos.y][fallbackPos.x] = 'witch';
      witchPos = fallbackPos;
    }
  }

  if (witchPos) occupied.push(witchPos);
}

// Enemies (scaled)
for (let i = 0; i < enemyCount; i++) {
  const enemyTile = isBossFloor && i === 0 ? 'enemy-boss' : 'enemy';
  const p = placeInRoom(enemyTile, {
    avoid: occupied,
    minDistFrom: entrance,
    minDist: 2,
    tries: 1400,
  });
  if (p) occupied.push(p);
}

  // Lore + Storylines (scaled)
  // Lore is represented by bookshelf variants (bookshelf-a / bookshelf-b).
  for (let i = 0; i < bookshelfCount; i++) {
    const shelfVariant = rng() < 0.5 ? 'bookshelf-a' : 'bookshelf-b';
    const p = placeInRoom(shelfVariant, {
      avoid: occupied,
      minDistFrom: entrance,
      minDist: i === 0 ? 4 : 6,
      tries: 1600,
    });
    if (p) occupied.push(p);
  }

  for (let i = 0; i < storylineCount; i++) {
  let p = placeInRoom('storyline', {
    avoid: occupied,
    minDistFrom: entrance,
    minDist: i === 0 ? 4 : 7,
    tries: 3000,
  });

  // Fallback: if room placement somehow fails, force one onto any free floor tile.
  if (!p) {
    p = placeAnywhere('storyline', {
      avoid: occupied,
      allow: new Set(['floor']),
      minDistFrom: entrance,
      minDist: i === 0 ? 4 : 7,
      tries: 3000,
    });
  }

  if (p) occupied.push(p);
}


// Shrines (single chance per floor; tier-weighted).
// Tier weights + minimum floor are configured in game/shrine/shrineConfig.js.
if (floorLevel >= SHRINE_MIN_FLOOR && rng() < SHRINE_SPAWN_CHANCE_PER_FLOOR) {
  const tier = rollShrineTier(rng, SHRINE_TIER_WEIGHTS);
  const shrineTile =
    tier === "rare" ? "shrine-rare" : tier === "mythic" ? "shrine-mythic" : "shrine-common";

  const p = placeInRoom(shrineTile, {
    avoid: occupied,
    minDistFrom: entrance,
    minDist: 5,
    tries: 1800,
  });
  if (p) occupied.push(p);
}

// Wells (single chance per floor; lets the player discard a trinket).
// Spawn tuning is configured in game/well/wellConfig.js.
if (floorLevel >= WELL_MIN_FLOOR && rng() < WELL_SPAWN_CHANCE_PER_FLOOR) {
  const p = placeInRoom('well', {
    avoid: occupied,
    minDistFrom: entrance,
    minDist: 5,
    tries: 1800,
  });
  if (p) occupied.push(p);
}

// Decorative statues for sanctified floors.
// These reuse the same statue tiles as the secret rare-chest annex.
if (statueCount > 0 && rng() < moodCounts.statueSpawnChance) {
  for (let i = 0; i < statueCount; i++) {
    const statueTile = getRandomStatueTile(rng);

    let p = placeInRoom(statueTile, {
      avoid: occupied,
      minDistFrom: entrance,
      minDist: 3,
      tries: 1800,
      requireRoomy: false,
      allow: new Set(['floor']),
    });

    if (!p) {
      p = placeAnywhere(statueTile, {
        avoid: occupied,
        allow: new Set(['floor']),
        minDistFrom: entrance,
        minDist: 3,
        tries: 1800,
      });
    }

    if (p) occupied.push(p);
  }
}

map = maybeApplySanctifiedStatueSecret(map, {
  moodId,
  rng,
});

// Final safety: ensure the playable space is fully connected.
// (Post-processing + doors can occasionally partition the map.)
(function ensureAllConnected() {
  const dirs4 = [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ];
  const inBounds = (x, y) => x >= 0 && y >= 0 && x < width && y < height;
  const k = (x, y) => `${x},${y}`;

  // Treat locked doors as walls for connectivity; we want at least one keyless path.
  const passable = (x, y) => {
    const t = map[y]?.[x];
    return t !== undefined && t !== 'non-walkable' && t !== 'door-locked';
  };

  const flood = () => {
    const q = [];
    const seen = new Set();
    const points = [];

    if (passable(entrance.x, entrance.y)) {
      q.push([entrance.x, entrance.y]);
      seen.add(k(entrance.x, entrance.y));
    }

    while (q.length) {
      const [x, y] = q.shift();
      points.push({ x, y });
      for (const [dx, dy] of dirs4) {
        const nx = x + dx;
        const ny = y + dy;
        if (!inBounds(nx, ny)) continue;
        if (!passable(nx, ny)) continue;
        const kk = k(nx, ny);
        if (seen.has(kk)) continue;
        seen.add(kk);
        q.push([nx, ny]);
      }
    }
    return { seen, points };
  };

  const carveStep = (x, y) => {
    const t = map[y]?.[x];
    if (t === 'non-walkable') map[y][x] = 'corridor';
    else if (t === 'door-locked') map[y][x] = 'door-open';
    // Never overwrite special walkable tiles (chests, merchant, etc.).
  };

  const carveTunnelL = (a, b) => {
    // Randomize L-bend order (still deterministic due to rng).
    if (rng() < 0.5) {
      // Horizontal then vertical
      const y = a.y;
      const xFrom = a.x;
      const xTo = b.x;
      const dx = Math.sign(xTo - xFrom);
      for (let x = xFrom; x !== xTo; x += dx) carveStep(x, y);
      carveStep(xTo, y);

      const x = b.x;
      const yFrom = a.y;
      const yTo = b.y;
      const dy = Math.sign(yTo - yFrom);
      for (let yy = yFrom; yy !== yTo; yy += dy) carveStep(x, yy);
      carveStep(x, yTo);
    } else {
      // Vertical then horizontal
      const x = a.x;
      const yFrom = a.y;
      const yTo = b.y;
      const dy = Math.sign(yTo - yFrom);
      for (let y = yFrom; y !== yTo; y += dy) carveStep(x, y);
      carveStep(x, yTo);

      const y = b.y;
      const xFrom = a.x;
      const xTo = b.x;
      const dx = Math.sign(xTo - xFrom);
      for (let xx = xFrom; xx !== xTo; xx += dx) carveStep(xx, y);
      carveStep(xTo, y);
    }
  };

  // Repeatedly connect any unreachable passable tile to the reachable component.
  // This guarantees:
  // - entrance -> exit reachability
  // - all walkable regions are connected
  for (let safety = 0; safety < 50; safety++) {
    const { seen, points } = flood();

    // Find any passable tile not reachable.
    let target = null;
    outer: for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (!passable(x, y)) continue;
        if (!seen.has(k(x, y))) {
          target = { x, y };
          break outer;
        }
      }
    }
    if (!target) break; // fully connected

    // Find nearest reachable point to connect from.
    let best = null;
    let bestD = Infinity;
    for (const p of points) {
      const d = Math.abs(p.x - target.x) + Math.abs(p.y - target.y);
      if (d < bestD) {
        bestD = d;
        best = p;
      }
    }
    if (!best) break;

    carveTunnelL(best, target);
  }
})();

// 7) Secret annex: corridor ONLY on outer border + 3x3 room + chest + 4 braziers
(function addSecretAnnexBorderOnly() {
  if (isSpiderBossArena || isSingleRoomArenaLayout || moodLayout.noCorridors) return;
  const SECRET = 'hidden-corridor';
  const ROOM_W = 3;
  const ROOM_H = 3;

  const DIRS4 = [
  [1, 0], [-1, 0],
  [0, 1], [0, -1],
];

const isWalkableDungeonTile = (t) => {
  // Anything that isn't void and isn't the corridor counts as "walkable stuff we don't want to touch".
  if (!t) return false;
  if (t === 'non-walkable') return false;
  if (t === SECRET) return false;
  return true;
};

const touchesForbiddenDungeon = (x, y, allowedA, allowedB) => {
  // allowedA and allowedB are the ONLY two walkable tiles we’re allowed to touch:
  // - the entrance tile (anchor)
  // - the room doorway tile
  for (const [dx, dy] of DIRS4) {
    const nx = x + dx, ny = y + dy;
    if (!inBounds(nx, ny, width, height)) continue;

    const t = map[ny][nx];
    if (!isWalkableDungeonTile(t)) continue;

    // If neighbor is the allowed one, fine.
    const ok =
      (allowedA && nx === allowedA.x && ny === allowedA.y) ||
      (allowedB && nx === allowedB.x && ny === allowedB.y);

    if (!ok) return true; // touches forbidden dungeon tile
  }
  return false;
};


  const isBorder = (x, y) =>
    x === 0 || y === 0 || x === width - 1 || y === height - 1;

  const inwardOfBorder = (x, y) => {
    if (x === 0) return { x: 1, y };
    if (x === width - 1) return { x: width - 2, y };
    if (y === 0) return { x, y: 1 };
    return { x, y: height - 2 }; // y === height-1
  };

      // Which edge is a border cell on?
  const edgeOf = (x, y) => {
    if (x === 0) return 'left';
    if (x === width - 1) return 'right';
    if (y === 0) return 'up';
    return 'down'; // y === height - 1
  };

  // Can this border cell be part of the corridor WITHOUT creating extra connections?
  // Rules:
  // - start border tile must touch ONLY the anchor (ax,ay)
  // - end border tile must touch ONLY the doorway (doorX,doorY)
  // - middle border tiles must have inward neighbor = non-walkable
  const canUseBorderCell = (bx, by, sx, sy, ex, ey, ax, ay, doorX, doorY) => {
    if (!isBorder(bx, by)) return false;

    const t = map[by]?.[bx];
    if (t !== 'non-walkable' && t !== SECRET) return false;

    const inward = inwardOfBorder(bx, by);
    const inwardTile = map[inward.y]?.[inward.x];

    const isStart = (bx === sx && by === sy);
    const isEnd = (bx === ex && by === ey);

    if (isStart) return inward.x === ax && inward.y === ay;
    if (isEnd) return inward.x === doorX && inward.y === doorY;

    return inwardTile === 'non-walkable';
  };

  // Find a random wiggly path ALONG THE BORDER from start -> end (can turn corners).
  // Returns array of {x,y} including start and end, or null if impossible.
  const findWigglyBorderPath = (sx, sy, ex, ey, ax, ay, doorX, doorY, maxSteps = 800) => {
    const key = (x, y) => `${x},${y}`;
    const visited = new Set();
    const parent = new Map();

    const stack = [{ x: sx, y: sy }];
    visited.add(key(sx, sy));

    let steps = 0;

    while (stack.length && steps++ < maxSteps) {
      const cur = stack[stack.length - 1];

      if (cur.x === ex && cur.y === ey) {
        // reconstruct path
        const path = [];
        let p = { x: ex, y: ey };
        path.push(p);

        while (!(p.x === sx && p.y === sy)) {
          const prev = parent.get(key(p.x, p.y));
          if (!prev) return null;
          path.push(prev);
          p = prev;
        }

        path.reverse();
        return path;
      }

      // border neighbors only (up/down/left/right)
      const nbrs = [];
      for (const [dx, dy] of DIRS4) {
        const nx = cur.x + dx;
        const ny = cur.y + dy;
        if (!inBounds(nx, ny, width, height)) continue;
        if (!isBorder(nx, ny)) continue;
        if (visited.has(key(nx, ny))) continue;

        if (!canUseBorderCell(nx, ny, sx, sy, ex, ey, ax, ay, doorX, doorY)) continue;

        nbrs.push({ x: nx, y: ny });
      }

      // shuffle neighbors so the path bends/turns randomly
      for (let i = nbrs.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        [nbrs[i], nbrs[j]] = [nbrs[j], nbrs[i]];
      }

      if (nbrs.length) {
        const nxt = nbrs[0];
        parent.set(key(nxt.x, nxt.y), cur);
        visited.add(key(nxt.x, nxt.y));
        stack.push(nxt);
      } else {
        // dead end -> backtrack
        stack.pop();
      }
    }

    return null;
  };

  const isAnchorTile = (t) => t === 'floor' || t === 'corridor';
  const isEmpty = (x, y) => map[y]?.[x] === 'non-walkable';

  const placeRoom = (rx, ry) => {
    // floor
    for (let yy = ry; yy < ry + ROOM_H; yy++) {
      for (let xx = rx; xx < rx + ROOM_W; xx++) map[yy][xx] = 'floor';
    }
    // 4 random decorative statues (unique within this room)
  const statueIds = pickUniqueStatueIds(rng, 4);
  const corners = [
    [rx, ry],         // top-left
    [rx + 2, ry],     // top-right
    [rx, ry + 2],     // bottom-left
    [rx + 2, ry + 2], // bottom-right
  ];

  corners.forEach(([sx, sy], i) => {
    const id = statueIds[i] ?? (1 + Math.floor(rng() * STATUE_POOL_SIZE));
    map[sy][sx] = `statue-${id}`;
  });
    // center chest
map[ry + 1][rx + 1] = rng() < 0.25 ? 'chest-rare-closed' : 'chest-rare-locked';
  };

    const canPlaceRoom = (rx, ry) => {
    if (rx < 0 || ry < 0 || rx + ROOM_W > width || ry + ROOM_H > height) return false;

    // 1) The 3x3 room interior must not overwrite the dungeon.
    for (let yy = ry; yy < ry + ROOM_H; yy++) {
      for (let xx = rx; xx < rx + ROOM_W; xx++) {
        const t = map[yy][xx];
        // allow overwriting hidden corridor tiles if they pass through the future room area
        if (t !== 'non-walkable' && t !== SECRET) return false;
      }
    }

    // 2) NEW: 1-tile buffer ring around the room must be empty too.
    // This prevents the secret room from touching the main dungeon.
    for (let yy = ry - 1; yy <= ry + ROOM_H; yy++) {
      for (let xx = rx - 1; xx <= rx + ROOM_W; xx++) {
        // skip out-of-bounds (edges are fine)
        if (xx < 0 || yy < 0 || xx >= width || yy >= height) continue;

        // skip the room interior itself
        const insideRoom =
          xx >= rx && xx < rx + ROOM_W &&
          yy >= ry && yy < ry + ROOM_H;
        if (insideRoom) continue;

        const t = map[yy][xx];

        // Ring must be walls/void only (or SECRET, which we’re okay overwriting).
        // If it’s floor/corridor/door/etc, the room would "touch" the dungeon.
        if (t !== 'non-walkable' && t !== SECRET) return false;
      }
    }

    return true;
  };


  // 1) Find “entrances”: a normal dungeon tile next to a BORDER tile that is non-walkable.
  // This guarantees the corridor touches the dungeon exactly once.
  const entrances = [];
  const dirs4 = [[1,0],[-1,0],[0,1],[0,-1]];

  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      if (!isAnchorTile(map[y][x])) continue;

      for (const [dx, dy] of dirs4) {
        const bx = x + dx;
        const by = y + dy;

        if (!isBorder(bx, by)) continue;   // must step onto border
        if (!isEmpty(bx, by)) continue;    // border tile must be empty so we can carve

        entrances.push({ ax: x, ay: y, sx: bx, sy: by });
      }
    }
  }

  if (!entrances.length) return;

  // shuffle entrances
  for (let i = entrances.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [entrances[i], entrances[j]] = [entrances[j], entrances[i]];
  }

  // 2) Try each entrance until we manage to carve a border-only corridor + place room.
  for (const ent of entrances) {
    const { ax, ay, sx, sy } = ent;

    // Determine which edge we are on (left/right/top/bottom)
    const edge =
      sx === 0 ? 'left' :
      sx === width - 1 ? 'right' :
      sy === 0 ? 'up' : 'down';

        // Candidate endpoints: ANY empty border cell.
    // This lets the corridor turn corners and travel across multiple edges.
    const endpoints = [];
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (!isBorder(x, y)) continue;
        if (x === sx && y === sy) continue;
        if (map[y][x] !== 'non-walkable') continue; // must be empty border
        endpoints.push({ x, y });
      }
    }

    // shuffle endpoints
    for (let i = endpoints.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [endpoints[i], endpoints[j]] = [endpoints[j], endpoints[i]];
    }


        for (const ep of endpoints) {
      const ex = ep.x;
      const ey = ep.y;

      // The room must attach to the END edge (not the start edge)
      const endEdge = edgeOf(ex, ey);

      // Decide room position (just inside end edge) and doorway (inside tile adjacent to border)
      let rx, ry, doorX, doorY;
      if (endEdge === 'left') {
        rx = 1; ry = ey - 1; doorX = 1; doorY = ey;
      } else if (endEdge === 'right') {
        rx = width - 4; ry = ey - 1; doorX = width - 2; doorY = ey;
      } else if (endEdge === 'up') {
        rx = ex - 1; ry = 1; doorX = ex; doorY = 1;
      } else {
        rx = ex - 1; ry = height - 4; doorX = ex; doorY = height - 2;
      }

      if (!canPlaceRoom(rx, ry)) continue;

            // Find a wiggly border path from start -> end (turns allowed)
      const path = findWigglyBorderPath(sx, sy, ex, ey, ax, ay, doorX, doorY, 1200);
      if (!path) continue;

      // Carve corridor along the path
      const carved = [];
      let ok = true;

      for (const p of path) {
        if (!canUseBorderCell(p.x, p.y, sx, sy, ex, ey, ax, ay, doorX, doorY)) {
          ok = false;
          break;
        }
        map[p.y][p.x] = SECRET;
        carved.push({ x: p.x, y: p.y });
      }

      if (!ok) {
        for (const p of carved) map[p.y][p.x] = 'non-walkable';
        continue;
      }


      // Success: place room & doorway
      placeRoom(rx, ry);
      map[doorY][doorX] = 'floor';

      // Mark the anchor tile so you can recognize the entry
      if (map[ay][ax] === 'floor' || map[ay][ax] === 'corridor') {
        map[ay][ax] = 'secret-entrance';
      }

      // Cleanup any stray SECRET tiles (paranoia safety)
      const carvedSet = new Set(carved.map(p => `${p.x},${p.y}`));
      for (let yy = 0; yy < height; yy++) {
        for (let xx = 0; xx < width; xx++) {
          if (map[yy][xx] === SECRET && !carvedSet.has(`${xx},${yy}`)) {
            map[yy][xx] = 'non-walkable';
          }
        }
      }

      return; // only one secret annex per floor
    }
  }
})();

return map;
}

